import { createLinkMatcherWithRegExp } from '@lexical/react/LexicalAutoLinkPlugin';
import isURL from 'validator/es/lib/isURL';
import { z } from 'zod';

export const uriSchema = z
  .string()
  .refine((url) => isURL(url, { require_protocol: false }), { message: 'Please enter a valid URL' })
  .or(z.string().url('Please enter a valid URL'))
  .or(z.string().regex(/^(#|\/)[a-zA-Z0-9/_-]+$/));

export const urlSchema = z
  .string()
  .refine((url) => isURL(url, { require_protocol: false }), { message: 'Please enter a valid URL' });

type ValidUrlOptions = {
  allowAnyProtocol?: boolean;
};

export function isValidUrl(url: string, options?: ValidUrlOptions) {
  if (options?.allowAnyProtocol) {
    return uriSchema.safeParse(url).success;
  }
  return urlSchema.safeParse(url).success;
}

const SUPPORTED_URL_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'sms:', 'tel:']);
const PROTOCOL_REGEX = /^[a-z]+:/;

export function sanitizeUrl(url: string): string {
  if (url.startsWith('#')) return url;
  try {
    let sanitizedUrl = url;

    if (!sanitizedUrl.match(PROTOCOL_REGEX)) {
      sanitizedUrl = sanitizedUrl.startsWith('//') ? `https:${sanitizedUrl}` : `https://${sanitizedUrl}`;
    }

    const parsedUrl = new URL(sanitizedUrl);
    if (!SUPPORTED_URL_PROTOCOLS.has(parsedUrl.protocol)) {
      return 'about:blank';
    }

    return sanitizedUrl;
  } catch {
    return 'about:blank';
  }
}

export function getUrlTarget(url: string): string | undefined {
  if (url.startsWith('http:') || url.startsWith('https:')) {
    return '_blank';
  }
  return undefined;
}

const URL_REGEX =
  /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/;

const EMAIL_REGEX =
  /(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/;

export const MATCHERS = [
  createLinkMatcherWithRegExp(URL_REGEX, (text) => {
    return text;
  }),
  createLinkMatcherWithRegExp(EMAIL_REGEX, (text) => {
    return `mailto:${text}`;
  }),
];
